import { useEffect, useMemo } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { getDatum, getDatumZeit } from "@accredo/fe-shared/services/formatService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useContractActions } from "../../hooks/useContractActions.jsx";
import PropTypes from "prop-types";
import { DownloadOutlined, SwapHorizOutlined } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";

export default function ContractsTable({
  onRowClick = () => {},
  onAssignedClerkClick = () => {},
  onBulkAssignedClerkClick = () => {},
  reloadToken = 0,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { state: contractsState, setState: setContractsState } =
    useContractActions();

  const tableKey = "contracts";
  const storageKey = "v2_table_contracts";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Verträge",
        route: `/data/vertrags`,
        exportRoute: `/data/vertrags/export`,
        exportFilename: `vertraege_export`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "created_at", order: "desc" }],
          include: ["gesellschaft", "sachbearbeiter"],
        },
        onStateChange: setContractsState,
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const {
    tableStates,
    updateTableState,
    fetchData,
    exportData,
    tableExportStates,
  } = useMultiTableData(tablesConfig, enqueueSnackbar);

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reloadToken > 0) {
      fetchData(tableKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  const rows = contractsState?.data?.data || [];

  const columns = [
    {
      accessorKey: "gesellschaft.name",
      header: "Gesellschaft",
      minSize: 250,
    },
    {
      accessorKey: "vertragsnummer",
      header: "Vertragsnummer",
    },
    {
      accessorKey: "vertragsart",
      header: "Vertragsart",
    },
    {
      accessorKey: "letzter_ol_import",
      header: "Objektliste - letzter Import",
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return <span>{getDatumZeit(objectToRender.letzter_ol_import)}</span>;
      },
    },
    {
      accessorKey: "beginn",
      header: "Beginn",
      maxSize: 120,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return <span>{getDatum(objectToRender.beginn)}</span>;
      },
    },
    {
      accessorKey: "assigned_clerk",
      header: "Sachbearbeiter",
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return (
          <Box
            component="button"
            onClick={(event) => {
              event.stopPropagation();
              onAssignedClerkClick(objectToRender);
            }}
            sx={{
              border: "none",
              background: "transparent",
              padding: 0,
              margin: 0,
              color: "primary.main",
              cursor: "pointer",
              font: "inherit",
              textAlign: "left",
              textDecoration: "underline",
            }}
          >
            {objectToRender?.sachbearbeiter?.full_name || "Nicht zugewiesen"}
          </Box>
        );
      },
    },
    {
      accessorKey: "ablauf",
      header: "Ablauf",
      maxSize: 120,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return <span>{getDatum(objectToRender.ablauf)}</span>;
      },
    },
  ];

  const handleExportExcel = () => {
    exportData(tableKey, "excel");
  };

  const toolbarActions = [
    {
      type: "button",
      label: "Sachbearbeiter ersetzen",
      icon: <SwapHorizOutlined />,
      onClick: onBulkAssignedClerkClick,
      variant: "tertiary",
    },
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: tableExportStates[tableKey].isExporting ? (
        <CircularProgress color="primary" size={32} thickness={5} />
      ) : (
        <DownloadOutlined />
      ),
      onClick: handleExportExcel,
      disabled: tableExportStates[tableKey].isExporting,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      enableTopToolbar={true}
      toolbarActions={toolbarActions}
      onRowClick={onRowClick}
    />
  );
}

ContractsTable.propTypes = {
  onRowClick: PropTypes.func,
  onAssignedClerkClick: PropTypes.func,
  onBulkAssignedClerkClick: PropTypes.func,
  reloadToken: PropTypes.number,
};
